import React, { useState, useContext } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Paper, Typography, Tabs, Tab } from '@mui/material';
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import PieChartMoviment from '../components/common/PieChartMoviment.js';
import ConceptList from '../components/common/ConceptList.js';
import ConceptHierarchyList from '../components/common/ConceptHierarchyList.js';
import AppContext from '../components/AppContext.js';
import Saldo from '../components/common/Saldo.js';
import DataLoader from '../components/common/DataLoader.js';
import config from '../config.js';
import AnualBalanceListGlobal from '../components/dashboard-anual/AnualBalanceListGlobal.js';
import MonthlyBalanceListGlobal from '../components/dashboard-anual/MonthlyBalanceListGlobal.js';
import CercadorMovimentsGlobal from '../components/dashboard-anual/CercadorMovimentsGlobal.js';
import { calculaAgrupacioConceptes } from '../functions/agrupacio-conceptes.js';
import PieChartAgrupacio from '../components/common/PieChartAgrupacio.js';

const DashboardGlobal = () => {
  const { ctxApp, setCtxApp } = useContext(AppContext);
  const [tabValue, setTabValue] = useState(ctxApp?.dashboardGlobalTabSelected || 0);
  const [viewMode, setViewMode] = useState(ctxApp?.dashboardViewMode || 'estalvi');

  const { userapp: paramUserapp } = useParams();
  const userapp = paramUserapp;

  const navigate = useNavigate();
  const location = useLocation();

  const handleToggleViewMode = () => {
    setViewMode(prev => {
      let next;
      if (prev === 'estalvi') next = 'inversiones';
      else if (prev === 'inversiones') next = 'actual';
      else next = 'estalvi';
      setCtxApp(c => ({ ...c, dashboardViewMode: next }));
      return next;
    });
  };

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    axios.get(`${config.apiBaseUrl}/v1/resumglobal/${userapp}`, {
      headers: { Authorization: `${jwtToken}` }
    })
      .then((response) => {
        onData(response);

        const newCtxData = {
          ...ctxApp,
          firstPage: location.pathname,
          numConceptePendent: response.data.numConceptePendent || 0,
          numDesglosPendent: response.data.numDesglosPendent || 0,
          numPendentValidar: response.data.numPendentValidar || 0,
          numPendentTotal: (response.data.numConceptePendent || 0) + (response.data.numDesglosPendent || 0) + (response.data.numPendentValidar || 0)
        };
        setCtxApp(newCtxData);
      })
      .catch((err) => {
        onError(err, 'cargar dades');
      })
      .finally(() => onFinally());
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCtxApp(prev => ({ ...prev, dashboardGlobalTabSelected: newValue }));
  };

  const gotoDetail = (anio) => {
    setCtxApp(prev => ({ ...prev, dashboardAnualTabSelected: undefined }));
    navigate(`${anio}`);
  }

  return (
    <DataLoader id={'global'} fetchFunction={fetchFunction}>
      {(data) => {
        const listAgrupacioDespeses = data
          ? Object.values(data.mapAgregatMovimentsGlobal)
            .filter(v => v.importAgregat.saldo < 0)
          : [];
        const listAgrupacioIngresos = data
          ? Object.values(data.mapAgregatMovimentsGlobal)
            .filter(v => v.importAgregat.saldo > 0)
          : [];
        const agrupacioConceptes = calculaAgrupacioConceptes(data.mapAgregatConceptes);
        const listAgrupacioConceptesDespeses = data
          ? Object.values(agrupacioConceptes)
            .filter(v => v.tipus === 'DESPESA')
          : [];
        const listAgrupacioConceptesIngressos = data
          ? Object.values(agrupacioConceptes)
            .filter(v => v.tipus === 'INGRES')
          : [];
        const numAnys = Object.values(data.mapAgregatAnual).length;

        // Calcular desinversiones e inversiones del total de conceptos
        let desinversiones = 0;
        let inversiones = 0;
        if (data.mapAgregatConceptes) {
          Object.values(data.mapAgregatConceptes).forEach(item => {
            if (item.concepte?.tipus === 'INVERSIO') {
              const val = item.importAgregat?.saldo || 0;
              if (val > 0) {
                desinversiones += val;
              } else {
                inversiones += val;
              }
            }
          });
        }
        const divisorAnys = numAnys || 1;
        const mitjaDesinversiones = desinversiones / divisorAnys;
        const mitjaInversiones = inversiones / divisorAnys;

        // Calcular valores según el modo de vista seleccionado
        let globalSaldo, globalIngressos, globalDespeses;
        let mitjaSaldo, mitjaIngressos, mitjaDespeses;

        if (viewMode === 'estalvi') {
          globalSaldo = data.agregat?.importAgregat?.saldoEstalvi;
          globalIngressos = data.agregat?.importAgregat?.ingressos;
          globalDespeses = data.agregat?.importAgregat?.despeses;

          mitjaSaldo = data.agregat?.importAgregat?.mitjaSaldoEstalvi;
          mitjaIngressos = data.agregat?.importAgregat?.mitjaIngressos;
          mitjaDespeses = data.agregat?.importAgregat?.mitjaDespeses;
        } else if (viewMode === 'inversiones') {
          globalSaldo = data.agregat?.importAgregat?.inversions;
          globalIngressos = undefined;
          globalDespeses = undefined;

          mitjaSaldo = data.agregat?.importAgregat?.mitjaInversions;
          mitjaIngressos = undefined;
          mitjaDespeses = undefined;
        } else { // actual
          globalSaldo = data.agregat?.importAgregat?.saldo;
          globalIngressos = (data.agregat?.importAgregat?.ingressos || 0) + desinversiones;
          globalDespeses = (data.agregat?.importAgregat?.despeses || 0) + inversiones;

          mitjaSaldo = data.agregat?.importAgregat?.mitjaSaldo;
          mitjaIngressos = (data.agregat?.importAgregat?.mitjaIngressos || 0) + mitjaDesinversiones;
          mitjaDespeses = (data.agregat?.importAgregat?.mitjaDespeses || 0) + mitjaInversiones;
        }

        return (
          <>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <Typography variant="h5">
                Dashboard Global
              </Typography>

            </Grid>

            <Grid container spacing={2} sx={{ marginTop: '10px' }}>
              <Saldo titol="Saldo Global"
                size={{ xs: 12, sm: 6, md: 4 }}
                saldo={globalSaldo}
                ingressos={globalIngressos}
                despeses={globalDespeses}
                mode={viewMode}
                onToggleMode={handleToggleViewMode}
                showToggleIcon={true} />

              <Saldo titol="Mitja Anual"
                size={{ xs: 12, sm: 6, md: 4 }}
                saldo={mitjaSaldo}
                ingressos={mitjaIngressos}
                despeses={mitjaDespeses}
                mode={viewMode}
                onToggleMode={handleToggleViewMode}
                showToggleIcon={true} />

              {/* Tabbar */}
              <Grid size={12}>
                <Paper>
                  <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
                    <Tab label="Anys" />
                    <Tab label="Conceptes" />
                    <Tab label="Conceptes (Jeràrquic)" />
                    <Tab label="Mesos" />
                    <Tab label="Cercador Moviments" />
                  </Tabs>

                  {tabValue === 0 && (
                    <AnualBalanceListGlobal years={data.mapAgregatAnual} gotoDetail={gotoDetail} defaultViewMode={viewMode} />
                  )}
                  {tabValue === 1 && (
                    <ConceptList concepts={data.mapAgregatConceptes} tipus="global" viewMode={viewMode} />
                  )}
                  {tabValue === 2 && (
                    <ConceptHierarchyList userapp={userapp} tipus="global" viewMode={viewMode} />
                  )}
                  {tabValue === 3 && (
                    <MonthlyBalanceListGlobal months={data.mapAgregatMesos} numAnys={numAnys} defaultViewMode={viewMode} />
                  )}
                  {tabValue === 4 && (
                    <CercadorMovimentsGlobal months={data.mapAgregatMesos} numAnys={numAnys} />
                  )}

                </Paper>
              </Grid>

              {/* Gráficos de ingresos y gastos */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <PieChartMoviment data={listAgrupacioDespeses} title="Despeses Principals" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <PieChartMoviment data={listAgrupacioIngresos} title="Ingressos Principals" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <PieChartAgrupacio data={listAgrupacioConceptesDespeses} title="Despeses Agrupades" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <PieChartAgrupacio data={listAgrupacioConceptesIngressos} title="Ingressos Agrupats" />
              </Grid>
            </Grid>
          </>
        );
      }}
    </DataLoader>
  );
};

export default DashboardGlobal;
